import {
  CELL_FILTER,
  CELL_RENDERER,
  ENTITY_TYPES,
  FieldList,
  IFormGroupRules,
  IFormRules,
  IOrderAddPendingInventoryItemVenue,
  MEASURE_TRANSACTION,
  SORT_ORDERS,
  SupportedAppCodes,
  WIDGET_COMPONENT,
  XUX_GRID_ACTIONS,
} from '@app/shared/types';
import { get, isEmpty, keys } from 'lodash';

import { OrderAddPendingFormService } from '@shared/services';
import { ICellRendererParams } from 'ag-grid-community';

import { DecimalNumber, stringCaseInsensitiveComparator } from '@shared/common';
import { COLUMN_ALIGN_RIGHT } from '@shared/constants/column-align-definition';
import { ListColumn } from '@shared/types';
import { generateDecimalRegexp } from '@shared/constants';
import { ORDER_GRID_COL_TITLES } from '@containers/orders/containers/edit/order-form-col-title-contansts';
import {
  BUTTON_FLEX_ORDER,
  DEFAULT_GRID_HEIGHT,
} from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/constants';
import { OrderAddPendingContainerComponent } from '@app/containers/orders/containers/add-pending/order-add-pending.container';
import {
  getCommonFloatInputRenderParams,
  receivedOrderCountLimit,
  unitCountLimit,
} from '@shared/services/orders/orders.definition';
import { generateUnitTextV2 } from '@shared/utils';
import { I18n } from '@app/i18n/strings-keys';

export const createFormRules = (
  formService: OrderAddPendingFormService,
  editor: OrderAddPendingContainerComponent,
): IFormRules => {
  const pagePanelSectionFields: FieldList = {
    item_list_section: {
      title: '',
      widget: WIDGET_COMPONENT.panel_group,
    },
  };

  const pageAllPanelSection: IFormGroupRules = {
    name: I18n.Common.Page,
    fields: keys(pagePanelSectionFields),
  };
  const canManageInventoryItem: boolean = editor.permissionService.canManage(
    ENTITY_TYPES.INVENTORY_ITEM,
    '',
    SupportedAppCodes.XDM,
  );

  const itemListFields: FieldList = {
    'virtual.item_list': {
      widget: WIDGET_COMPONENT.edit_grid_custom,
      title: I18n.Forms.GridForm.ItemList,
      class: 'order-items-grid navigate-grid order-add-items-grid',
      widgetData: {
        showLoader: formService.listLoader$,
        useVisibleItemCountDescription: true,
        showSearch: true,
        multiselect: false,
        orderControls: false,
        disableColumnSelector: true,
        gridMinHeight: DEFAULT_GRID_HEIGHT,
        userGridOptions: {
          deltaRowDataMode: true,
          noGridHeight: true,
          suppressMovableColumns: true,
          suppressHorizontalScroll: false,
          suppressClickEdit: true,
          suppressRowTransform: true,
          suppressRowClickSelection: true,
          defaultColDef: {
            editable: true,
          },
          overlayNoRowsTemplate: I18n.Common.NoRowsToShow,
          // this can prevent list scroll when update row data
          immutableData: true,
          disableColumnSelector: true,
          rowClassRules: {
            'ag-row-focus-auto': (row: any): boolean => {
              return row.data.isFocus;
            },
          },
        },
        columns: [
          {
            defaultView: true,
            path: 'name',
            title: ORDER_GRID_COL_TITLES.itemName,
            sortable: true,
            sort: SORT_ORDERS.ASC,
            sortingOrder: [SORT_ORDERS.ASC, SORT_ORDERS.DESC],
            comparator: stringCaseInsensitiveComparator,
            filterParams: {
              quickFilter: true,
            },
          },
          {
            defaultView: true,
            path: 'code',
            title: I18n.Forms.GridForm.ItemID,
            sortable: true,
            comparator: stringCaseInsensitiveComparator,
            filterParams: {
              quickFilter: true,
            },
          },
          {
            defaultView: true,
            title: I18n.Forms.GridForm.MajorCategory,
            path: 'reporting_category_major.entity_id',
            labelPath: 'reporting_category_major.name',
            selected: false,
            filterType: CELL_FILTER.entity,
            filterParams: {
              quickFilter: false,
              values: formService.majorItems$,
              filterOptions: ['containsAny'],
              suppressSelectAllControl: true,
            },
          },
          {
            defaultView: true,
            title: I18n.Forms.GridForm.MinorCategory,
            path: 'reporting_category_minor.entity_id',
            labelPath: 'reporting_category_minor.name',
            selected: false,
            filterType: CELL_FILTER.entity,
            filterParams: {
              quickFilter: false,
              values: formService.minorItems$,
              filterOptions: ['containsAny'],
              suppressSelectAllControl: true,
            },
          },
          {
            path: 'gl_account.entity_id',
            labelPath: 'gl_account.name',
            defaultView: true,
            title: ORDER_GRID_COL_TITLES.glAccountName,
            selected: false,
            filterType: CELL_FILTER.entity,
            filterParams: {
              quickFilter: false,
              values: formService.gLAItems$,
              filterOptions: ['containsAny'],
              suppressSelectAllControl: true,
            },
          },
          {
            path: 'site_availability',
            defaultView: true,
            title: I18n.Forms.GridForm.AddToList,
            width: 120,
            cellRenderer: CELL_RENDERER.link_renderer,
            valueGetter: () =>
              formService.i18nService.instant(I18n.Forms.GridForm.AddToList),
            cellRendererParams: {
              onClick: (
                inventoryItem: IOrderAddPendingInventoryItemVenue,
                params: ICellRendererParams,
              ): void => {
                if (
                  get(inventoryItem, 'site_availability') &&
                  !isEmpty(formService.getVendorMeasure(inventoryItem))
                ) {
                  formService.addToOderPending(inventoryItem);
                } else if (
                  canManageInventoryItem ||
                  (get(inventoryItem, 'site_availability', false) &&
                    !isEmpty(formService.getVendorMeasures(inventoryItem)))
                ) {
                  inventoryItem.isFocus = true;

                  formService.showModalDialogHandler(
                    inventoryItem,
                    params.node,
                  );
                }
              },
            },
            cellClass: (params: ICellRendererParams): string => {
              const inventoryItem: IOrderAddPendingInventoryItemVenue = get(
                params,
                'data',
              );
              if (
                canManageInventoryItem ||
                (get(inventoryItem, 'site_availability', false) &&
                  !isEmpty(formService.getVendorMeasures(inventoryItem)))
              ) {
                return 'high-light';
              }

              return 'disabled-link';
            },
          },
        ],
        eventHandlers: {
          [XUX_GRID_ACTIONS.GRID_READY]: formService.getGridReadyHandler(),
        },
      },
    },
    'virtual.added_item_list': {
      widget: WIDGET_COMPONENT.edit_grid_custom,
      title: I18n.Forms.GridForm.AddedItemsBrackets,
      minItems: 0,
      action: formService.inlineRemoveAction$,
      descriptionObs: formService.addedItemListDescriptionObs$,
      widgetData: {
        inlineRemove: true,
        showSearch: true,
        multiselect: true,
        orderControls: false,
        disableColumnSelector: true,
        userGridOptions: {
          deltaRowDataMode: true,
          suppressMovableColumns: true,
          suppressHorizontalScroll: false,
          suppressClickEdit: true,
          suppressRowTransform: true,
          suppressRowClickSelection: false,
          defaultColDef: {
            editable: true,
          },
          overlayNoRowsTemplate: I18n.Common.NoRowsToShow,
          // this can prevent list scroll when update row data
          immutableData: true,
          disableColumnSelector: true,
          singleClickEdit: true,
        },
        columns: [
          {
            defaultView: true,
            path: 'name',
            title: ORDER_GRID_COL_TITLES.itemName,
            sortable: true,
            comparator: stringCaseInsensitiveComparator,
            filterParams: {
              quickFilter: true,
            },
          },
          {
            path: 'gl_account.name',
            defaultView: true,
            title: ORDER_GRID_COL_TITLES.glAccountName,
          },
          {
            defaultView: true,
            path: 'measure_description',
            title: ORDER_GRID_COL_TITLES.measureEntityId,
            cellRenderer: (params: ICellRendererParams) =>
              generateUnitTextV2(
                {
                  description: params.value,
                  factor_value: params.data?.measure_factor_value,
                },
                {
                  numberFormatService: formService.numberFormatService,
                  measureTransactionType: MEASURE_TRANSACTION.Order,
                  notNeedAddSign: true,
                },
              ),
          },
          {
            path: 'count',
            defaultView: true,
            minWidth: 120,
            maxWidth: 120,
            title: ORDER_GRID_COL_TITLES.initialItemCount,
            cellRenderer: ({ value }) => {
              if (isEmpty(value)) return '';

              return `${value < 0 ? 0 : value}`;
            },
            ...COLUMN_ALIGN_RIGHT,
          },
          {
            path: 'initial_item_cost',
            defaultView: true,
            maxWidth: 120,
            title: ORDER_GRID_COL_TITLES.initialItemCost,
            valueFormatter:
              formService.numberFormatService.getNumberFormatter(true),
            ...COLUMN_ALIGN_RIGHT,
          },
          {
            path: 'initial_total_cost',
            defaultView: true,
            maxWidth: 180,
            title: ORDER_GRID_COL_TITLES.initialTotalCost,
            cellRenderer: (params: ICellRendererParams) => {
              const initial_count = get(params, 'data.count', 0);
              const initial_item_cost = get(
                params,
                'data.initial_item_cost',
                0,
              );
              const count = initial_count < 0 ? 0 : initial_count;
              const value = new DecimalNumber(count).mul(initial_item_cost);

              return formService.numberFormatService.getNumberFormatter(true)({
                value,
              });
            },
            ...COLUMN_ALIGN_RIGHT,
          },
          {
            path: 'count',
            title: ORDER_GRID_COL_TITLES.quantityReceived,
            defaultView: true,
            editable: true,
            type: 'number',
            valueFormatter:
              formService.numberFormatService.getNumberFormatter(false),
            cellRenderer: CELL_RENDERER.float_input_renderer,
            cellRendererParams: (): ListColumn['cellRendererParams'] =>
              getCommonFloatInputRenderParams({
                precision: formService.stateService.getNumberPrecision(),
                acceptableInputRegex: generateDecimalRegexp(
                  formService.stateService.getNumberPrecision(),
                  false,
                ),
                min: receivedOrderCountLimit.minimum,
                max: receivedOrderCountLimit.maximum,
              }),
            ...COLUMN_ALIGN_RIGHT,
            minWidth: 120,
            maxWidth: 120,
          },
          {
            title: ORDER_GRID_COL_TITLES.receivedUnitCost,
            defaultView: true,
            path: 'item_cost',
            editable: true,
            minWidth: 120,
            maxWidth: 120,
            type: 'number',
            valueFormatter:
              formService.numberFormatService.getNumberFormatter(true),
            cellRenderer: CELL_RENDERER.price_input_renderer,
            cellRendererParams: (): ListColumn['cellRendererParams'] => {
              return {
                ...getCommonFloatInputRenderParams({
                  precision: formService.stateService.getCurrencyPrecision(),
                  acceptableInputRegex: generateDecimalRegexp(
                    formService.stateService.getCurrencyPrecision(),
                    true,
                  ),
                  min: unitCountLimit.minimum,
                  max: unitCountLimit.maximum,
                }),
              };
            },
            ...COLUMN_ALIGN_RIGHT,
          },
          {
            path: 'total_cost',
            defaultView: true,
            minWidth: 180,
            maxWidth: 180,
            title: ORDER_GRID_COL_TITLES.receivedTotalCost,
            cellRenderer: (params: ICellRendererParams) => {
              const count = get(params, 'data.count', 0);
              const item_cost = get(params, 'data.item_cost', 0);

              return formService.numberFormatService.getNumberFormatter(true)({
                value: new DecimalNumber(count).mul(item_cost),
              });
            },
            ...COLUMN_ALIGN_RIGHT,
          },
        ],
        buttons: [
          {
            caption: I18n.Common.Actions,
            value: 'actions',
            flexOrder: BUTTON_FLEX_ORDER.LEFTMOST,
            hidden: isEmpty,
            childItems: [
              {
                caption: I18n.Common.Remove,
                value: 'Remove',
                click: formService.removeFromOderPending.bind(formService),
              },
            ],
          },
        ],
      },
      validators: [formService.getAddedItemsRequireValidator().bind(editor)],
    },
  };

  const ItemListGroup: IFormGroupRules = {
    name: I18n.Common.General,
    id: 'item_list_section',
    fields: keys(itemListFields),
  };

  return {
    fields: {
      ...pagePanelSectionFields,
      ...itemListFields,
    },
    groups: [pageAllPanelSection, ItemListGroup],
    defaultGroup: false,
    virtual: {
      type: 'object',
      properties: {
        inventory_items: {
          type: 'array',
        },
        item_list: {
          type: 'array',
        },
        added_item_list: {
          type: 'array',
        },
      },
    },
  };
};
